import React, { useState, useEffect, useMemo, useRef } from 'react'
import { Box, TextField, Button, Grid, InputLabel, Typography, Divider, FormControl,
  Select, MenuItem , Autocomplete, List, ListItem, IconButton, ListItemIcon, ListItemText
} from '@mui/material';
import { useForm, Controller } from "react-hook-form";
import { useSnackbar } from 'notistack';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import ControlPointIcon from '@mui/icons-material/ControlPoint';
import { savePass } from 'services/passes';
import { getLibraries } from 'services/property';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import DeleteIcon from '@mui/icons-material/Delete';

const AddPass = ({ passDetails = null, onPassAdded }) => {
  const defaultFormFields = useMemo(() => ({
    price: 0,
    title: '',
    passType: '',
    propertyType: '',
    propertyId: null,
  }),[]);

  const [features, setFeatures] = useState([]);
  const [libraries, setLibraries] = useState([]);
  const { enqueueSnackbar } = useSnackbar();
  const { handleSubmit, control, formState: { errors }, reset } = useForm({
    mode: 'onTouched',
    defaultValues: {...defaultFormFields}
  });
  const featureInputRef = useRef(null);

  useEffect(() => {
    if(passDetails){
      const data = {...(Object.fromEntries(Object.entries(defaultFormFields).map(([key, value]) => {
        return [key, passDetails[key] || value];
      })))};      
      data['propertyId'] = passDetails?.property

      if(Array.isArray(passDetails?.features)){
        setFeatures(passDetails.features)
      }
      reset({...data});
    }
  }, [passDetails, defaultFormFields, reset]);

  useEffect(() => {
    getLibraryTypes();
    return () => {}
  }, []);

  function getLibraryTypes() {
    getLibraries().then(({response, error}) => {
      if(response) {
        setLibraries([...response]);
      }else{
        enqueueSnackbar((response?.message || error), {variant: 'error'});
      }
    })
  }
  
  const onSubmit = (data) => {

    if(features.length === 0) {
      enqueueSnackbar("Add atlease one featue", {variant: 'error'});
      return;
    }
    savePass({
        ...data,
        propertyId: (data?.propertyId?.libraryId || data?.propertyId?.propertyId),
        features,
        id: passDetails?.id,
      }).then(({response, error}) => {
        if(response){
          enqueueSnackbar((`Pass ${passDetails?.id ? 'updated' : 'added'} successfully`), {variant: 'success'});
          reset({...defaultFormFields});
          setFeatures([]);
          onPassAdded();
        } else {
          enqueueSnackbar((response?.message || error), {variant: 'error'});
        }
      });
  }

  const AddFeature = () => {
    if(features.length >= 6 ) {
      enqueueSnackbar("Can not add more than 6 featues", {variant: 'error'});
      featureInputRef.current.value = '';
    }else if(featureInputRef.current.value){
      setFeatures([...features, featureInputRef.current.value]);
      featureInputRef.current.value = '';
    } else{
      enqueueSnackbar("Add Featues", {variant: 'error'});
    }
  }

  const deleteFeature = (index) => {
    const temp = [...features];
    temp.splice(index, 1);
    setFeatures([...temp]);
  }

  return (
    <Box noValidate component="form"  onSubmit={handleSubmit(onSubmit)} sx={{ mt: 3 }} >
      <Grid container columnSpacing={2} rowSpacing={2} justifyContent="center">
        <Grid item xs={12} sm={12} md={10} lg={10}  >
          <Grid item container columnSpacing={2}>

            <Grid item xs={12} sm={12} md={12} lg={12} >
              <Controller
                control={control}
                name="title"
                id="title"
                rules={{ required: "Enter title" }}
                render={({field}) => (
                  <TextField
                    {...field}
                    margin="normal"
                    label="Title"
                    required
                    error={!!errors?.title}
                    size="small"
                    fullWidth
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={12} md={12} lg={12} sx={{ mt: 3 }}>
              <Controller
                control={control}
                name="passType"
                id="passType"
                required
                rules={{ required: "Select pass type" }}
                render={({field}) => (
                  <FormControl {...field} required fullWidth error={!!errors?.passType}>
                    <InputLabel id="passtype-label" size="small">Pass Type</InputLabel>
                    <Select
                      labelId="passtype-label"
                      id="passtype-select-id"
                      label="Pass Type"
                      name="passType"
                      required
                      size="small"
                      value={field.value}
                      onChange={field.onChange}
                      
                    >
                      <MenuItem value='Day Pass'>Day Pass</MenuItem>
                      <MenuItem value='Week Pass'>Week Pass</MenuItem>
                      <MenuItem value='Month Pass'>Month Pass</MenuItem>
                      <MenuItem value='Quarter Pass'>Quarter Pass</MenuItem>
                      <MenuItem value='Year Pass'>Year Pass</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
            
            <Grid item xs={12} sm={12} md={12} lg={12} sx={{ mt: 3.5 }}>
              <Controller
                control={control}
                name="propertyType"
                id="propertyType"
                required
                rules={{ required: "Select property type" }}
                render={({field}) => (
                  <FormControl {...field} required fullWidth error={!!errors?.propertyType}>
                    <InputLabel id="propertyType-label" size="small">Property Type</InputLabel>
                    <Select
                      labelId="propertyType-label"
                      id="propertyType-select-id"
                      label="Property Type"
                      name="propertyType"
                      required
                      size="small"
                      value={field.value}
                      onChange={field.onChange}
                    >
                      <MenuItem value='library'>Library</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={12} sm={12} md={12} lg={12}  sx={{mt: 3.5}}>
              <Controller
                control={control}
                name="propertyId"
                id="propertyId"
                rules={{ required: "Select property" }}
                render={({field }) => (
                  <Autocomplete
                    {...field}
                    // disablePortal
                    options={libraries}
                    size="small"
                    name="propertyId"
                    fullWidth
                    // value={field.value || ''}
                    getOptionLabel={(option) => option?.name || option}
                    isOptionEqualToValue={(option, value) => option?.libraryId === value?.libraryId}
                    onChange={(event, newValue) => {field.onChange(newValue)}}
                    renderInput={(params) => <TextField {...params} {...field} error={!!errors?.propertyId}  required label="Property Name" />}
                  />
                )}
                onChange={([, data]) => data}
              />
            </Grid>

            <Grid item xs={12} sm={12} md={12} lg={12} sx={{ mt: 2 }}>
              <Controller
                control={control}
                name="price"
                id="price"
                rules={{ required: "Enter price" }}
                render={({field}) => (
                  <TextField
                    {...field}
                    type="number"
                    margin="normal"
                    label="Price"
                    required
                    error={!!errors?.price}
                    // helperText={errors.lastName?.message}
                    size="small"
                    fullWidth
                    // InputProps={{
                    //   endAdornment: <InputAdornment position="start">%</InputAdornment>,
                    // }}
                  />
                )}
              />
            </Grid>

            <Grid container item xs={12} sm={12} md={12} lg={12} sx={{mt: 4}}>
              <Grid item xs={12} sm={12} md={12} lg={12} >
                <Typography variant="h6" gutterBottom component="div">Plan Benefits</Typography>
                <Divider />
              </Grid>

              <Grid item xs={12} sm={12} md={12} lg={12} >
                {features?.length <= 5 ? (
                  <>
                    <TextField
                      multiline
                      rows={2}
                      margin="normal"
                      label="Add text here"
                      size="small"
                      fullWidth
                      inputRef={featureInputRef}
                    />
                    <Button
                      type="button"
                      variant="contained"
                      sx={{ mt: 4, mb: 2 }}
                      // startIcon={<ControlPointIcon />}
                      color="success"
                      onClick={AddFeature}
                      >
                      Add
                    </Button>
                  </>
                ) : null}
              
                <List dense={true}>
                  {features.map((ele, index) => (
                    <ListItem key={index}
                      secondaryAction={
                        <IconButton edge="end" aria-label="delete" onClick={() => deleteFeature(index)}>
                          <DeleteIcon />
                        </IconButton>
                      }
                    >
                    <ListItemIcon>
                      <ArrowRightIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={ele}
                        // secondary={secondary ? 'Secondary text' : null}
                      />
                    </ListItem>
                  ))}
                </List>
                
              </Grid>
            </Grid>

            <Grid
              container
              direction="row"
              justifyContent="center"
              alignItems="center"
            >
              <Button
                type="submit"
                variant="contained"
                sx={{ mt: 3, mb: 2, textTransform: 'inherit' }}
                startIcon={ passDetails ? <ModeEditIcon />: <ControlPointIcon />}
                color="primary"
              >
                {passDetails ? 'Update' : 'Create'}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}
 
export default React.memo(AddPass);
